/**
 * Generates a short, human-readable ID from a UUID
 *
 * Uses a simple hash function to convert a UUID into a 4-digit number.
 * This is deterministic (same UUID always produces the same short ID).
 *
 * Collision risk is very low for datasets < 10,000 items.
 * For production use with larger datasets, consider a backend auto-increment solution.
 *
 * @param uuid - Full UUID string (e.g., "7333070e-1f6d-4b2f-93cd-0fc7d46c6189")
 * @param length - Desired length of short ID (default: 4)
 * @returns Short numeric ID as string (e.g., "4729")
 *
 * @example
 * const shortId = getShortId("7333070e-1f6d-4b2f-93cd-0fc7d46c6189");
 * console.log(shortId); // "4729"
 */
export const getShortId = (uuid: string, length: number = 4): string => {
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Get positive number and limit to desired length
  const shortId = Math.abs(hash) % Math.pow(10, length);

  // Pad with zeros if needed
  return shortId.toString().padStart(length, '0');
};
